import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  HardHat,
  Hammer,
  Home as HomeIcon,
  Wrench,
  Building2,
  PhoneCall
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  COMPANY,
  STATS,
  SERVICES,
  PROJECTS,
  TESTIMONIALS,
  ACCREDITATIONS,
  VALUES
} from "@/data/mock";

const SERVICE_ICONS = {
  bricklaying: Hammer,
  extensions: HomeIcon,
  "new-builds": Building2,
  renovations: Wrench,
  commercial: HardHat
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6 animate-fade-up">
            <h1
              data-testid="hero-heading"
              className="font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink text-balance"
            >
              Built next door.
              <br />
              <span className="text-brand-terracotta">Built to last.</span>
            </h1>

            <p className="mt-6 text-lg text-brand-inkSoft max-w-xl leading-relaxed">
              Brickwork, extensions, refurbishments and ground-up new builds across Berkshire — done
              by the people you can wave at on the school run. Honest fixed prices, one site
              manager, proper paperwork.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                asChild
                data-testid="hero-cta-quote"
                className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white text-[15px] transition-transform hover:-translate-y-0.5 shadow-[0_12px_32px_rgba(192,86,64,0.28)]"
              >
                <Link to="/contact">
                  Get a free quote <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-cta-projects"
                className="rounded-full h-12 px-6 bg-transparent border-brand-ink/20 text-brand-ink hover:bg-brand-bgAlt"
              >
                <Link to="/projects">
                  See our work <ArrowUpRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>

            <div className="mt-10 flex items-center gap-5">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-brand-bg bg-brand-bgAlt flex items-center justify-center text-[11px] font-medium text-brand-inkSoft"
                  >
                    {["AS", "PT", "ML", "RB"][i - 1]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-brand-ochre">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                  <span className="ml-2 text-sm text-brand-ink font-medium">4.9 / 5</span>
                </div>
                <p className="text-xs text-brand-inkSoft mt-0.5">From 180+ verified neighbours</p>
              </div>
            </div>
          </div>

          {/* HERO IMAGE COMPOSITION */}
          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] md:aspect-[5/6] rounded-3xl overflow-hidden border border-brand-line shadow-[0_30px_80px_rgba(42,45,40,0.18)]">
              <img
                src={PROJECTS[0].image}
                alt={`${PROJECTS[0].title} by Neighbours Brick & Build`}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-4 rounded-2xl bg-brand-bg/95 backdrop-blur px-4 py-3 border border-brand-line">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-brand-inkSoft">
                    Latest project
                  </p>
                  <p className="font-display font-medium text-brand-ink">
                    {PROJECTS[0].title}
                  </p>
                </div>
                <Link
                  to={`/projects/${PROJECTS[0].id}`}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-terracotta text-white hover:bg-brand-terracottaDark transition-colors"
                  data-testid="hero-image-cta"
                >
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            {/* floating callout removed by request */}
          </div>
        </div>
      </section>

      {/* STATS / ACCREDITATIONS BAND */}
      <section className="border-y border-brand-line bg-brand-bgSage">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <div key={s.label} data-testid={`stat-${s.label}`}>
              <div
                className={`font-display text-4xl md:text-5xl font-semibold tracking-tighter ${
                  ["text-brand-terracotta", "text-brand-sageDark", "text-brand-ochreDark"][i % 3]
                }`}
              >
                {s.value}
              </div>
              <div className="text-sm text-brand-inkSoft mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-brand-line/70 overflow-hidden hidden">
          <div className="flex gap-10 py-5 whitespace-nowrap animate-marquee text-brand-inkSoft text-sm uppercase tracking-[0.22em]">
            {[...ACCREDITATIONS, ...ACCREDITATIONS].map((a, i) => (
              <span key={i} className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-terracotta" /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-brand-bgOchreSoft">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
              What we build
            </p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink max-w-2xl">
              From a single course of brick to a whole new home.
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-brand-ink font-medium hover:text-brand-terracotta"
            data-testid="services-link-all"
          >
            All services <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = SERVICE_ICONS[s.slug] || HardHat;
            // bento spans
            const span =
              idx === 0
                ? "md:col-span-4 md:row-span-2"
                : idx === 1
                ? "md:col-span-2"
                : idx === 2
                ? "md:col-span-2"
                : idx === 3
                ? "md:col-span-3"
                : "md:col-span-3";
            const tall = idx === 0 ? "md:min-h-[520px]" : "md:min-h-[260px]";
            return (
              <Link
                to={`/services#${s.slug}`}
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className={`group relative overflow-hidden rounded-3xl border border-brand-line bg-brand-bgAlt ${span} ${tall} flex flex-col`}
              >
                <div className="absolute inset-0">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/30 to-transparent" />
                </div>
                <div className="relative mt-auto p-6 md:p-7 text-white">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-[0.18em]">
                      <Icon className="w-3.5 h-3.5" /> {s.title.split(" ")[0]}
                    </div>
                    <span className="w-9 h-9 rounded-full bg-brand-terracotta inline-flex items-center justify-center transition-transform group-hover:-translate-y-1">
                      <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-2xl md:text-3xl font-medium tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-white/80 max-w-md">{s.short}</p>
                </div>
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="bg-brand-bgSageSoft border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
              Why neighbours
            </p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink">
              The kind of builder you’d trust with the spare key.
            </h2>
            <p className="mt-5 text-brand-inkSoft leading-relaxed max-w-md">
              We’re a small crew from your part of Berkshire. We turn up when we say, we leave the place
              tidy, and we answer the phone after 5pm.
            </p>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {VALUES.map((v, i) => {
              const accent = [
                "before:bg-brand-terracotta",
                "before:bg-brand-sage",
                "before:bg-brand-ochre"
              ][i % 3];
              return (
                <div
                  key={v.title}
                  className={`relative rounded-2xl bg-white border border-brand-line p-6 hover:border-brand-terracotta/40 hover:-translate-y-0.5 transition-all overflow-hidden before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1.5 ${accent}`}
                  data-testid={`value-${v.title}`}
                >
                  <h3 className="font-display text-xl text-brand-ink font-medium tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-brand-inkSoft text-[15px] leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROJECTS PREVIEW */}
      <section className="bg-brand-bgTerracottaSoft">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
              Recent work
            </p>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink">
              Built around Berkshire.
            </h2>
          </div>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-brand-ink font-medium hover:text-brand-terracotta"
            data-testid="projects-link-all"
          >
            All projects <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {PROJECTS.slice(0, 3).map((p) => (
            <Link
              to={`/projects/${p.id}`}
              key={p.id}
              data-testid={`home-project-${p.id}`}
              className="group rounded-2xl overflow-hidden bg-white border border-brand-line hover:border-brand-terracotta/40 transition-all"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <h3 className="font-display text-xl font-medium tracking-tight text-brand-ink">
                  {p.title}
                </h3>
                <ArrowUpRight className="w-4 h-4 text-brand-terracotta shrink-0 transition-transform group-hover:-translate-y-0.5" />
              </div>
            </Link>
          ))}
        </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-brand-ink text-brand-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-brand-ochre font-medium">
                Kind words from the street
              </p>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight">
                Real neighbours. Real builds.
              </h2>
            </div>
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-2 font-medium text-brand-bg hover:text-brand-ochre"
              data-testid="home-testimonials-link"
            >
              All reviews <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((t) => (
              <div
                key={t.name}
                data-testid={`home-testimonial-${t.name}`}
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-7 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-1 text-brand-ochre mb-4">
                  {[...Array(t.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="font-display text-lg leading-relaxed text-brand-bg/90">
                  “{t.quote}”
                </p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="font-medium">{t.name}</p>
                  <p className="text-sm text-brand-bg/60">{t.project}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/861de20d-72a6-47d7-860f-aae5d16b275d/images/aff86068c9a4d469bd0c2b2cccfd5183e6c3b279c4dd419e990cbe6c06646649.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bg via-brand-bg/95 to-brand-bg/70" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
              Ready when you are
            </p>
            <h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold tracking-tighter text-brand-ink leading-none">
              Let’s build something
              <br />
              <span className="text-brand-terracotta">you’ll love living in.</span>
            </h2>
            <p className="mt-5 text-brand-inkSoft max-w-xl">
              Free site visit, fixed-price written quote within a week. No pressure, no nonsense.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3 md:items-end">
            <Button
              asChild
              data-testid="cta-section-quote"
              className="rounded-full h-14 px-8 bg-brand-terracotta hover:bg-brand-terracottaDark text-white text-base shadow-[0_18px_50px_rgba(192,86,64,0.3)] transition-transform hover:-translate-y-0.5"
            >
              <Link to="/contact">
                Request a free quote <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-2 text-brand-ink font-medium hover:text-brand-terracotta"
              data-testid="cta-section-call"
            >
              <PhoneCall className="w-4 h-4" /> Or call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
